import './styles.scss'

import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Divider, Form, Row } from 'antd'

import Translator from '@/components/B2becTranslation'
import CustomButton from '@/components/CustomButton'
import { NotificationManager } from '@/components/Notification'
import ProductListWithPagination from '@/components/ProductListWithPagination'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectIsFetchingMachinesList,
  selectMachineActionStatus,
  selectMachinesList,
  selectMachinesListTotal,
} from '@/store/selectors/machineSelector'
import { getSelectedMachineData } from '@/store/selectors/serviceCaseSelector'
import { getMachinesList } from '@/store/slices/machineSlice'
import { setSelectedMachineInfo } from '@/store/slices/serviceCaseSlice'
import useDeviceDetect from '@/hooks/useDeviceDetect'
import { ASYNC_STATUS } from '@/libs/constants'

import SelectNewMachineBlock from '../../components/SelectNewMachineBlock'

const MachineSelectStep = ({ currentTab, handleChangeTab }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { isMobile } = useDeviceDetect()

  const selectedMachineData = useAppSelector(getSelectedMachineData)
  const isFetchingMachinesList = useAppSelector(selectIsFetchingMachinesList)
  const machinesList = useAppSelector(selectMachinesList)
  const total = useAppSelector(selectMachinesListTotal)
  const actionStatus = useAppSelector(selectMachineActionStatus)

  const {
    id: machineId,
    serialNumber,
    isExcludedMachine,
    validateStatus,
  } = selectedMachineData

  const onChangeTabHandler = useCallback(() => {
    if (isExcludedMachine) {
      return
    }

    handleChangeTab(+currentTab + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [handleChangeTab, currentTab, isExcludedMachine])

  const onSelectMachineHandler = useCallback(
    (machine) => {
      dispatch(
        setSelectedMachineInfo({
          id: machine?.materialNumber,
          name: machine?.name,
          image: machine?.imgUrl,
          serialNumber: machine?.serialNumber,
          faqList: [],
          isExcludedMachine: false,
          validateStatus: ASYNC_STATUS.SUCCESS,
        }),
      )

      setTimeout(() => {
        window.scrollTo(0, 0)
        handleChangeTab(+currentTab + 1)
      }, 1000)
    },
    [handleChangeTab, currentTab, dispatch],
  )

  const isDisableNextStepButton = isExcludedMachine || validateStatus === ASYNC_STATUS.PENDING

  const getMachineListHandler = useCallback(
    (currentPage, pageSize) => {
      dispatch(
        getMachinesList({
          pageNumber: currentPage,
          pageSize,
          sortOrder: 'ByOrderDateDesc',
        }),
      )
    },
    [dispatch],
  )

  useEffect(() => {
    if (currentTab !== 1 && !machineId && !serialNumber) {
      // prevent to change tab if user doesn't select machine
      NotificationManager.error({
        message: 'notification.error.mustSelectionMachine',
      })

      handleChangeTab(1)
    }
  }, [currentTab, handleChangeTab, machineId, serialNumber])

  return (
    <div className="service-step__machine">
      <div className="service-step__machine__description">
        <Translator
          isComponent
          htmlTags={[<div key="description" />]}
          value="service.selectMachine.description"
        />
      </div>

      {/* filter area */}
      <div className="service-step__machine__filter-area">
        {/* Filter area */}
      </div>

      {/* machine selection list */}
      <ProductListWithPagination
        isMachineSelection
        actionStatus={actionStatus}
        classNames="service-step__machine-list"
        getListHandler={getMachineListHandler}
        isLoading={isFetchingMachinesList}
        isMobile={isMobile}
        listData={machinesList}
        total={total}
        onSelectMachineHandler={onSelectMachineHandler}
      />

      {/* find and select machine by id */}
      <div className="service-step__search-machine">
        <Divider />
        <SelectNewMachineBlock selectedMachineData={selectedMachineData} />
      </div>

      {/* buttons wrapper */}
      <Row gutter={[24, 24]}>
        <Divider />
        <Col lg={{ span: 8, push: 16 }} md={12} span={24}>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldError }) => {
              const serialError = getFieldError('serialNumber')
              const isDisabled = (() => {
                if (!machineId) {
                  return true
                }
                return serialError.length > 0 || isDisableNextStepButton
              })()
              return (
                <CustomButton
                  block
                  disabled={isDisabled}
                  onClick={onChangeTabHandler}
                >
                  {t('service.button.next')}
                </CustomButton>
              )
            }}
          </Form.Item>
        </Col>
      </Row>
    </div>
  )
}

export default MachineSelectStep
